/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { createContext, use } from "react";
import { logger } from "matrix-js-sdk/lib/logger";
import * as Sentry from "@sentry/react";

import { type ElementCallError } from "../utils/errors";

/** A Graylog server's GELF HTTP input, at https://<host>[:port]/gelf. */
export interface GraylogConfig {
  host: string;
  port?: number;
  facility?: string;
  timeout?: number;
}

/** Fields sent with every message, flattened into GELF `_` fields. */
export type GraylogContext = Record<string, unknown>;

/** Where Element Call reports call failures for the operator to find. */
export interface Graylog {
  logCriticalError(
    message: string,
    error: Error | ElementCallError,
    context?: GraylogContext,
  ): void;
  logError(
    message: string,
    error?: Error | string,
    context?: GraylogContext,
  ): void;
  logWarning(message: string, context?: GraylogContext): void;
  /**
   * Logs a warning at most once per `intervalMs` for the same `key`, for
   * events that can repeat many times a second.
   */
  logWarningThrottled(
    key: string,
    intervalMs: number,
    message: string,
    context?: GraylogContext,
  ): void;
  logInfo(message: string, context?: GraylogContext): void;
  /** The same server, with `context` added to every message. */
  withContext(context: GraylogContext): Graylog;
}

/** Reports nothing: there is no Graylog server, as in tests and stories. */
export const nullGraylog: Graylog = {
  logCriticalError: () => {},
  logError: () => {},
  logWarning: () => {},
  logWarningThrottled: () => {},
  logInfo: () => {},
  withContext: () => nullGraylog,
};

/**
 * Reports to the Graylog server in `config`.
 * @param config The server to send to.
 * @param context Fields describing where Element Call runs, sent with every
 *   message. The caller gathers them, so that nothing here reads the page.
 * @param source The GELF `host` field: the name of the page's host.
 */
export function createGraylog(
  config: GraylogConfig,
  source: string,
  context: GraylogContext,
): Graylog {
  const port = config.port ? `:${config.port}` : "";
  const server: GelfServer = {
    url: `https://${config.host}${port}/gelf`,
    source,
    facility: config.facility || "7ion-call",
    timeout: config.timeout || 5000,
    lastThrottled: new Map(),
  };
  logger.info(`Graylog reporting to ${server.url}`);
  return new GelfGraylog(server, { ...context, facility: server.facility });
}

interface GelfServer {
  url: string;
  source: string;
  facility: string;
  timeout: number;
  /** Shared by every context, so that throttling holds across them. */
  lastThrottled: Map<string, number>;
}

enum GelfLevel {
  CRITICAL = 2,
  ERROR = 3,
  WARNING = 4,
  INFO = 6,
}

class GelfGraylog implements Graylog {
  public constructor(
    private readonly server: GelfServer,
    private readonly context: GraylogContext,
  ) {}

  public withContext(context: GraylogContext): Graylog {
    return new GelfGraylog(this.server, { ...this.context, ...context });
  }

  public logCriticalError(
    message: string,
    error: Error | ElementCallError,
    context?: GraylogContext,
  ): void {
    this.send(GelfLevel.CRITICAL, message, {
      ...errorContext(error),
      ...context,
      error_name: error.name,
      error_message: error.message,
      stack_trace: error.stack,
    });
  }

  public logError(
    message: string,
    error?: Error | string,
    context?: GraylogContext,
  ): void {
    const errorData =
      typeof error === "string"
        ? { error_message: error }
        : error instanceof Error
          ? {
              ...errorContext(error),
              error_name: error.name,
              error_message: error.message,
              stack_trace: error.stack,
            }
          : {};
    this.send(GelfLevel.ERROR, message, { ...context, ...errorData });
  }

  public logWarning(message: string, context?: GraylogContext): void {
    this.send(GelfLevel.WARNING, message, context);
  }

  public logWarningThrottled(
    key: string,
    intervalMs: number,
    message: string,
    context?: GraylogContext,
  ): void {
    const now = Date.now();
    const last = this.server.lastThrottled.get(key);
    if (last !== undefined && now - last < intervalMs) return;
    this.server.lastThrottled.set(key, now);
    this.logWarning(message, context);
  }

  public logInfo(message: string, context?: GraylogContext): void {
    this.send(GelfLevel.INFO, message, context);
  }

  private send(
    level: GelfLevel,
    message: string,
    extra: GraylogContext = {},
  ): void {
    const gelfMessage = {
      version: "1.1",
      host: this.server.source,
      short_message: message,
      full_message: message,
      timestamp: Date.now() / 1000,
      level,
      facility: this.server.facility,
      ...flatten({
        ...this.context,
        timestamp: new Date().toISOString(),
        ...extra,
      }),
    };
    fetch(this.server.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(gelfMessage),
      signal: AbortSignal.timeout(this.server.timeout),
    }).catch((err) => {
      // Every message would fail the same way while the server is unreachable.
      if (import.meta.env.DEV)
        logger.warn("Failed to send log to Graylog", err);
    });
  }
}

function errorContext(error: Error): GraylogContext {
  const context: GraylogContext = {};
  if (isElementCallError(error)) {
    context.errorCode = error.code;
    context.errorCategory = error.category;
    context.errorMessage = error.localisedMessage || error.message;
    if (error.cause instanceof Error) {
      context.cause_name = error.cause.name;
      context.cause_message = error.cause.message;
      context.cause_stack = error.cause.stack;
    }
  }
  const sentryEventId = Sentry.lastEventId();
  if (sentryEventId) context.sentryEventId = sentryEventId;
  return context;
}

function isElementCallError(error: Error): error is ElementCallError {
  return (
    "code" in error &&
    "category" in error &&
    "localisedTitle" in error &&
    typeof (error as ElementCallError).code === "string"
  );
}

/** GELF takes flat string fields with an `_` prefix. */
function flatten(obj: GraylogContext, prefix = "_"): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const gelfKey = key.startsWith("_") ? key : `${prefix}${key}`;
    if (value instanceof Date) {
      result[gelfKey] = value.toISOString();
    } else if (value === null || value === undefined) {
      result[gelfKey] = "";
    } else if (Array.isArray(value)) {
      result[gelfKey] = JSON.stringify(value);
    } else if (typeof value === "object") {
      Object.assign(result, flatten(value as GraylogContext, `${gelfKey}_`));
    } else {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      result[gelfKey] = String(value);
    }
  }
  return result;
}

const GraylogReactContext = createContext<Graylog | null>(null);

export const GraylogProvider = GraylogReactContext.Provider;

/**
 * Where to report call failures. Defaults to {@link nullGraylog}, so that
 * tests, stories and hosts without a Graylog server need no provider.
 */
export const useGraylog = (): Graylog =>
  use(GraylogReactContext) ?? nullGraylog;
